import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  InputAdornment,
  IconButton,
  Stack,
  Typography
} from '@mui/material';
import { colord } from 'colord';
import { CustomPicker } from 'react-color';
import { Saturation } from 'react-color/lib/components/common';
import { styles } from './StatusColorModal.styles';
import { IStoriesSettingsMessages } from '../../../../types';
import TagIcon from '@mui/icons-material/Tag';
import CloseIcon from '@mui/icons-material/Close';
import {
  ColorCodeFormat,
  RGBColorCode,
  RGBColorCodeKey,
  ColorFormat,
  Colors,
  readRecentColors,
  updateRecentColors,
  getDistinctColors,
  colorFormatOptions,
  converToHex
} from './StatusColorModal.utlis';
import { capitalDesignTokens } from '@opengov/capital-mui-theme';

const SVGrid = ({ hsl, hsv, onChange }: any) => (
  <Box sx={styles.colorModalContainer}>
    <Saturation hsl={hsl} hsv={hsv} onChange={onChange} />
  </Box>
);

export const GridOnlyPicker = CustomPicker(SVGrid);

export function StatusColorModal({
  open,
  onClose,
  initialColor = capitalDesignTokens.foundations.colors.white,
  onSave,
  messages
}: {
  open: boolean;
  onClose: () => void;
  initialColor?: string;
  onSave: (hex: string) => void;
  messages: IStoriesSettingsMessages;
}) {
  const [colorCodeFormat, setColorCodeFormat] = useState<ColorCodeFormat>(ColorFormat.HEX);
  const [hexColorCode, setHexColorCode] = useState<string>(
    initialColor ? colord(initialColor).toHex().toUpperCase() : ''
  );
  const [colorInput, setColorInput] = useState<string>(
    initialColor ? toFormatted(colord(initialColor).toHex(), ColorFormat.HEX) : ''
  );
  const [rgbColorCode, setRgbColorCode] = useState<RGBColorCode>({ red: 0, green: 0, blue: 0 });
  const [recentsColors, setRecentColors] = useState<string[]>(readRecentColors());
  useEffect(() => {
    if (open) {
      if (initialColor) {
        const hexCode = colord(initialColor).toHex().toUpperCase();
        setHexColorCode(hexCode);
        setColorCodeFormat(ColorFormat.HEX);
        setColorInput(toFormatted(hexCode, ColorFormat.HEX));
      } else {
        setHexColorCode('');
        setColorCodeFormat(ColorFormat.HEX);
        setColorInput('');
      }
    }
  }, [open, initialColor]);

  // keep text in sync when format or hex changes
  useEffect(() => {
    if (hexColorCode) {
      setColorInput(toFormatted(hexColorCode, colorCodeFormat));
    }
  }, [colorCodeFormat, hexColorCode]);

  function toFormatted(hex: string, format: ColorCodeFormat) {
    const color = colord(hex);
    if (!color.isValid()) return '';
    switch (format) {
      case ColorFormat.HEX:
        return color.toHex().toUpperCase().substring(1); // Remove the # symbol
      case ColorFormat.RGB: {
        const { r, g, b } = color.toRgb();
        setRgbColorCode({
          red: r,
          green: g,
          blue: b
        });
        return `rgb(${r}, ${g}, ${b})`;
      }
      default:
        return '';
    }
  }
  const isValid = useMemo(() => {
    // Check if the current text input is valid
    const parsed = converToHex(colorInput, colorCodeFormat);
    return parsed !== null;
  }, [colorInput, colorCodeFormat]);

  const onColorInputChange = (v: string) => {
    setColorInput(v);
  };
  const parseText = (v: string) => {
    const parsed = converToHex(v, colorCodeFormat);
    if (parsed) setHexColorCode(parsed.toUpperCase());
  };
  const handleSave = () => {
    if (!isValid) return;
    // Parse the current text input to get the final color
    const parsed = converToHex(colorInput, colorCodeFormat);
    if (!parsed) return;
    const canonical = parsed.toUpperCase();
    const next = getDistinctColors([canonical, ...recentsColors], 8);
    setRecentColors(next);
    updateRecentColors(next);
    onSave(canonical);
    onClose();
  };
  const parseRgbText = (v: string, color: RGBColorCodeKey) => {
    const nextColor = { ...rgbColorCode, [color]: parseInt(v) };
    const parsedColorString = `rgb(${nextColor.red}, ${nextColor.green}, ${nextColor.blue})`;
    const parsed = converToHex(parsedColorString, colorCodeFormat);
    if (parsed) setHexColorCode(parsed.toUpperCase());
  };

  const handleRGBChange = (v: string, color: RGBColorCodeKey) => {
    const RGB_PART = /^(?:25[0-5]|2[0-4]\d|1?\d?\d)$/; // 0–255
    const RGB_PART_EMPTY = /^(?:25[0-5]|2[0-4]\d|1?\d?\d)?$/; // 0–255 or empty
    if (RGB_PART.test(v)) {
      setRgbColorCode({ ...rgbColorCode, [color]: parseInt(v) });
    } else if (RGB_PART_EMPTY.test(v)) {
      setRgbColorCode({ ...rgbColorCode, [color]: 0 });
    }
  };
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth sx={styles.dialog} aria-labelledby="status-color-modal-title">
      <DialogTitle id="status-color-modal-title" sx={styles.dialogTitle}>
        {messages.editStatusColor}
        <IconButton aria-label="close" onClick={onClose} className="MuiDialog__close-button">
          <CloseIcon aria-hidden={true} />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box>
          <GridOnlyPicker
            color={hexColorCode}
            onChange={(color: any) => setHexColorCode(colord(color.hex).toHex().toUpperCase())}
          />
        </Box>

        {/* Color Code Row */}
        <Box sx={styles.colorCodeInputContainer}>
          <Typography sx={styles.labelText}>Color Code</Typography>
          <Box sx={styles.colorInputRow}>
            <Box
              aria-label="Selected color preview"
              sx={{
                ...styles.colorPreview,
                backgroundColor: hexColorCode,
                border: `1px solid ${hexColorCode}`
              }}
            />
            {colorCodeFormat === ColorFormat.HEX ? (
              <TextField
                value={colorInput}
                onChange={(e) => onColorInputChange(e.target.value)}
                onBlur={(e) => parseText(e.target.value)}
                error={!isValid}
                fullWidth
                size="small"
                variant="outlined"
                label="Color code"
                multiline={false}
                rows={1}
                sx={styles.colorInput}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      {colorCodeFormat === ColorFormat.HEX ? <TagIcon aria-hidden={true} /> : null}{' '}
                    </InputAdornment>
                  )
                }}
              />
            ) : (
              <Box sx={styles.rgbColorInputContainer}>
                <TextField
                  label="R"
                  value={rgbColorCode.red}
                  onChange={(e) => handleRGBChange(e.target.value, Colors.RED)}
                  onBlur={(e) => parseRgbText(e.target.value, Colors.RED)}
                />
                <TextField
                  label="G"
                  value={rgbColorCode.green}
                  onChange={(e) => handleRGBChange(e.target.value, Colors.GREEN)}
                  onBlur={(e) => parseRgbText(e.target.value, Colors.GREEN)}
                />
                <TextField
                  label="B"
                  value={rgbColorCode.blue}
                  onChange={(e) => handleRGBChange(e.target.value, Colors.BLUE)}
                  onBlur={(e) => parseRgbText(e.target.value, Colors.BLUE)}
                />
              </Box>
            )}

            <FormControl sx={styles.colorFormatSelect}>
              <InputLabel id="fmt-label">Format</InputLabel>
              <Select
                labelId="fmt-label"
                label="Format"
                value={colorCodeFormat}
                onChange={(e) => setColorCodeFormat(e.target.value as ColorCodeFormat)}
              >
                {colorFormatOptions.map((option: { value: string; text: string }) => {
                  return (
                    <MenuItem key={option.value} value={option.value}>
                      {option.text}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Box>
        </Box>

        {/* Last Used */}
        <Stack spacing={1} sx={styles.recentColorsMainContainer}>
          <Typography sx={styles.labelText}>Last Used</Typography>
          <Stack direction="row" sx={styles.recentColorsContainer} flexWrap="wrap">
            {recentsColors.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                No recent colors yet
              </Typography>
            )}
            {recentsColors.map((color) => (
              <Button
                key={color}
                onClick={() => setHexColorCode(color)}
                aria-label={`Use color ${color}`}
                sx={{ ...styles.colorPreview, backgroundColor: color, border: `1px solid ${color}`, '&:hover': { backgroundColor: color } }}
              />
            ))}
          </Stack>
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="secondary" variant="text">
          {messages.cancel}
        </Button>
        <Button onClick={handleSave} disabled={!isValid} variant="contained">
          {messages.save}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
